import { ExtractComponentProp } from '@approvalmax/types';
import { DotsFilledIcon } from '@approvalmax/ui';
import { ConfirmationPopup, Dropdown, Menu } from '@approvalmax/ui/src/components';
import { useSetAtom } from 'jotai';
import { memo, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { companyIdQueryParam } from 'shared/constants';
import {
    ConsentStatus,
    useCreateAmaxPayConsent,
    useDeleteAmaxPayOpenBankingBankAccounts,
    useGetAmaxPayOpenBankingBankAccounts,
    useReconnectAmaxPayOpenBankAccounts,
} from 'shared/data/webApp/v1';
import { getUrl, Path } from 'urlBuilder';

import { useMatchXeroPopupQueryParams } from '../../../../hooks/useMatchXeroPopupQueryParams';
import { useReadOnlyCompany } from '../../../../hooks/useReadOnlyCompany';
import { bankingAppLoginPopupState } from '../../../BankingAppLoginPopup/BankingAppLoginPopup.states';
import { messages } from './CardControls.messages';
import { StyledButton } from './CardControls.styles';
import type { CardControlsProps } from './CardControls.types';

export const CardControls = memo<CardControlsProps>((props) => {
    const { id, nickname, connected, hide, consentStatus, onRematchXeroBankAccount, setShowRenameForm } = props;

    const { companyId } = useParams<{ companyId: string }>();
    const isReadOnly = useReadOnlyCompany();
    const { setReMatch } = useMatchXeroPopupQueryParams();
    const [openConfirmation, setOpenConfirmation] = useState(false);
    const setBankingAppLoginPopupState = useSetAtom(bankingAppLoginPopupState);
    const { data: amaxPayOpenBankingBankAccounts } = useGetAmaxPayOpenBankingBankAccounts({ path: { companyId } });

    const { mutate: deleteAmaxPayOpenBankingBankAccounts, isLoading: isLoadingDeleteAmaxPayOpenBankingBankAccounts } =
        useDeleteAmaxPayOpenBankingBankAccounts({
            onSettled: () => {
                setOpenConfirmation(false);
            },
        });

    const { mutate: reconnectAmaxPayOpenBankAccounts } = useReconnectAmaxPayOpenBankAccounts({
        onSuccess: ({ data }) => {
            const bankAccount = amaxPayOpenBankingBankAccounts?.items.find((bankAccount) => bankAccount.id === id);

            if (bankAccount) {
                setBankingAppLoginPopupState({
                    consentId: data.id,
                    bankId: bankAccount.bank.id,
                    authorisationUrl: data.authorisationUrl,
                    createdAt: new Date(data.createdAt),
                });
            }
        },
    });

    const { mutate: createAmaxPayConsent } = useCreateAmaxPayConsent({
        onSuccess: ({ data }) => {
            const bankAccount = amaxPayOpenBankingBankAccounts?.items.find((bankAccount) => bankAccount.id === id);

            if (bankAccount) {
                setBankingAppLoginPopupState({
                    consentId: data.id,
                    bankId: bankAccount.bank.id,
                    authorisationUrl: data.authorisationUrl,
                    createdAt: new Date(data.createdAt),
                });
            }
        },
    });

    const handleReMatchXero = useCallback(() => {
        setReMatch('true');
        onRematchXeroBankAccount();
    }, [onRematchXeroBankAccount, setReMatch]);

    const handleReconnectAccount = useCallback(() => {
        if (consentStatus === ConsentStatus.Revoked) {
            const bankAccount = amaxPayOpenBankingBankAccounts?.items.find((bankAccount) => bankAccount.id === id);

            if (bankAccount) {
                void createAmaxPayConsent({
                    body: {
                        callbackUrl: `${getUrl(Path.connectBankAccountSuccess)}?${companyIdQueryParam}=${companyId}`,
                        bankId: bankAccount.bank.id,
                    },
                    params: { path: { companyId } },
                });
            }
        } else {
            void reconnectAmaxPayOpenBankAccounts({ params: { path: { companyId, bankAccountId: id } } });
        }
    }, [
        consentStatus,
        amaxPayOpenBankingBankAccounts?.items,
        id,
        createAmaxPayConsent,
        companyId,
        reconnectAmaxPayOpenBankAccounts,
    ]);

    const handleRemoveAccount = useCallback(() => {
        setOpenConfirmation(true);
    }, []);

    const handleRename = useCallback(() => {
        setShowRenameForm(true);
    }, [setShowRenameForm]);

    const items = useMemo(
        () => [
            {
                name: messages.changeNickname,
                disabled: isReadOnly,
                onClick: handleRename,
            },
            {
                name: messages.rematchXeroBankAccount,
                disabled: isReadOnly,
                onClick: handleReMatchXero,
            },
            {
                name: messages.reconnectAccount,
                disabled: isReadOnly || connected,
                onClick: handleReconnectAccount,
            },
            {
                name: messages.removeAccount,
                onClick: handleRemoveAccount,
            },
        ],
        [isReadOnly, handleRename, handleReMatchXero, connected, handleReconnectAccount, handleRemoveAccount]
    );

    const handleToggleLeaving = useCallback<ExtractComponentProp<typeof ConfirmationPopup, 'onToggle'>>((toggle) => {
        setOpenConfirmation(toggle);
    }, []);

    const handleConfirmDeletion = useCallback(() => {
        void deleteAmaxPayOpenBankingBankAccounts({ params: { path: { companyId, bankAccountId: id } } });
    }, [companyId, deleteAmaxPayOpenBankingBankAccounts, id]);

    return (
        <>
            <Dropdown
                closeOnClickInnerButton
                width='max-content'
                activator={
                    <StyledButton icon noPadding $hide={hide}>
                        <DotsFilledIcon />
                    </StyledButton>
                }
            >
                <Menu items={items} />
            </Dropdown>

            <ConfirmationPopup
                confirmMessage={messages.areYouSure({ nickname })}
                confirmButtonMessage={messages.delete}
                open={openConfirmation}
                confirmProgress={isLoadingDeleteAmaxPayOpenBankingBankAccounts}
                onToggle={handleToggleLeaving}
                onConfirm={handleConfirmDeletion}
            />
        </>
    );
});

CardControls.displayName = 'CardControls';
